import { Fragment, ComponentType, FC, useRef } from 'react';
import dynamic from 'next/dynamic';

import { ColorProvider } from '@bscom/contexts';
import { OptimizelyProvider } from 'main-contexts/optimizely';
import { renderContentfulEntry } from '@bscom/utilities/renderers';
import { PageSmoothScroll } from '@bscom/components/PageSmoothScroll';
import { NewBrandBaseNav } from '@bscom/components/NewBrandNavigation';

import { NewBrandBaseLayout } from 'layouts/NewBrandBaseLayout';
import { NewBrandNavigation } from 'components/NewBrandBusinessNav';
import { NewBrandPageProps } from 'main-types';

const BannerManager = dynamic(() =>
  import('@bscom/components/PromotionalBanner').then((mod) => mod.BannerManager)
);

const newModules: Record<string, ComponentType<any>> = {
  leftRightRichText: dynamic(
    () => import('components/content-blocks/NewBrandLeftRight')
  ),
  pageCheckTrustscore: dynamic(
    () => import('components/content-blocks/NewBrandPageCheckTrustscore')
  ),
  summaryColumns: dynamic(
    () => import('@bscom/content-blocks/NewBrandSummaries')
  ),
  pageResources: dynamic(
    () => import('@bscom/content-blocks/NewBrandPageResources')
  ),
  pageRichText: dynamic(
    () => import('@bscom/content-blocks/NewBrandPageRichtext')
  ),
  emailSignup: dynamic(
    () => import('@bscom/content-blocks/NewBrandEmailSignup')
  ),
  pageHeroTrustscore: dynamic(
    () => import('@bscom/content-blocks/NewBrandCheckTrustscore')
  ),
  iconTextList: dynamic(
    () => import('@bscom/content-blocks/NewBrandIconTextList')
  ),
  richTextBlockGrid: dynamic(
    () => import('@bscom/content-blocks/NewBrandRichTextBlockGrid')
  ),
  pageBusinessLogoSection: dynamic(
    () =>
      import('@bscom/content-blocks/NewBrandPageScrollingBusinessLogoSection')
  ),
  plansAddons: dynamic(
    () => import('@bscom/content-blocks/Plans/NewBrandPlansAddons')
  ),
  pageFaq: dynamic(() => import('@bscom/content-blocks/NewBrandPageFaq')),
  pageThreeColumnGrid: dynamic(
    () => import('@bscom/content-blocks/NewBrandPageColumnGrid')
  ),
  imageBackgroundRichText: dynamic(
    () => import('@bscom/content-blocks/ImageBackgroundRichText')
  ),
  pageScrollingLeftRight: dynamic(
    () => import('@bscom/content-blocks/ScrollingQuote')
  ),
  pageImageCarousel: dynamic(
    () => import('@bscom/content-blocks/NewBrandPageImageCarousel')
  ),
  pageComparePlansTable: dynamic(
    () => import('@bscom/content-blocks/Plans/NewBrandPlansCompareTable')
  ),
  pageCodeBlock: dynamic(
    () => import('components/content-blocks/NewBrandPageCodeBlock')
  ),
  variationContainer: dynamic(
    () => import('components/content-blocks/VariationContainer')
  ),
  pageInfoBox: dynamic(() => import('@bscom/content-blocks/PageInfoBox')),
  pageToggleRedirector: dynamic(
    () => import('@bscom/content-blocks/PageToggleRedirector')
  ),
  multiContentBox: dynamic(
    () => import('@bscom/content-blocks/PageMultiContentBox')
  ),
  pageTrustpilotForm: dynamic(() => import('@bscom/content-blocks/NewBrandPageTrustpilotForm')),
  pageTelevision: dynamic(() => import('@bscom/content-blocks/PageTelevision')),
  pagePricePlans: dynamic(() => import('@bscom/content-blocks/NewBrandPagePricePlans')),
  pagePriceAddOns: dynamic(() => import('@bscom/content-blocks/NewBrandPagePriceAddOns')),
  pageBrandCta: dynamic(() => import('@bscom/content-blocks/NewBrandPageBrandCta')),
};

const bannerModules = {
  pageCheckTrustscore: dynamic(
    () => import('components/content-blocks/NewBrandPageCheckTrustscore')
  ),
  pageHeroTrustscore: dynamic(
    () => import('@bscom/content-blocks/NewBrandCheckTrustscore')
  ),
  componentRichText: dynamic(() =>
    import('components/RichTextBanner').then((mod) => mod.RichTextBanner)
  ),
};

export const renderNewBrandBlock = renderContentfulEntry(newModules);

const renderBanner = renderContentfulEntry(bannerModules);

export const NewBrandPage = (props: NewBrandPageProps) => {
  let NavigationComponent: FC;

  switch (props.navigationStyle) {
    case 'regular':
      NavigationComponent = NewBrandNavigation;
      break;
    case 'logo-only':
      NavigationComponent = NewBrandBaseNav;
      break;
    case 'none':
      NavigationComponent = Fragment;
      break;
    default:
      NavigationComponent = NewBrandNavigation;
  }

  return (
    <ColorProvider value={props?.brandColor?.color}>
      <OptimizelyProvider optimizelyExperimentsKeys={props?.optimizelyProps}>
        <PageSmoothScroll />
        <NewBrandBaseLayout
          bannerJsx={
            props.banner && (
              <BannerManager>
                {({ hiddenBanners, hideBanner }) =>
                  hiddenBanners.includes(props.banner.sys.id)
                    ? null
                    : renderBanner({ ...props.banner, hideBanner })
                }
              </BannerManager>
            )
          }
          NavigationComponent={NavigationComponent}
        >
          {props.contentCollection.items.map((content, index) => (
            <Fragment key={index}>{renderNewBrandBlock(content)}</Fragment>
          ))}
        </NewBrandBaseLayout>
      </OptimizelyProvider>
    </ColorProvider>
  );
};
